// @ts-strict-ignore
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import AutomationsToolboxSortComponent from './AutomationsToolboxSortComponent';
import Search from 'antd/lib/input/Search';
import {
    selectSortedAutomationsToolboxAutomations,
    selectSortedFilteredAutomations,
} from '../../selectors/memoizedSelectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import AutomationsToolboxFilterComponent from './AutomationsToolboxFilterComponent';
import { categoryForAutomation, isSameSet } from '../../helpers';
import { Automation, AutomationCategory, AutomationType, IntegrationCapability } from '../../types';
import { AutomationInfoComponent } from '../automations/AutomationInfoComponent';
import { globalMessage } from '../../utils/globalMessage';
import { AppAutomationSettings } from '../automations/AppAutomationSettings';
import { AppComponentProps, StringKeyedDictionary } from '../../typescriptHelpers';

interface AutomationsListContainerProps extends AppComponentProps {
    onShowConnectIntegrations: (capabilityType: IntegrationCapability, focusedAutomationId: string) => void;
    scrollToAutomation?: AutomationType;
}

export const AutomationsListContainer: React.FC<AutomationsListContainerProps> = ({
    appId,
    orgId,
    onShowConnectIntegrations,
    scrollToAutomation,
}) => {
    const sortedAutomations = useSelector((state: RootState) =>
        selectSortedAutomationsToolboxAutomations(state, appId),
    );

    const [searchValue, setSearchValue] = React.useState('');
    const [filteredAutomations, setFilteredAutomations] = React.useState(sortedAutomations);
    const [automations, setAutomations] = React.useState(sortedAutomations);
    const [availableCategories, setAvailableCategories] = React.useState<AutomationCategory[]>([]);
    const [availableCapabilities, setAvailableCapabilities] = React.useState<IntegrationCapability[]>([]);
    const [categoryFilters, setCategoryFilters] = React.useState<AutomationCategory[]>([]);
    const [capabilityFilters, setCapabilityFilters] = React.useState<IntegrationCapability[]>([]);

    const automationRefRecords: StringKeyedDictionary<HTMLDivElement> = {};

    const clearFilters = () => {
        setCategoryFilters([]);
        setCapabilityFilters([]);
    };

    const setFilteredAndSearchedAutomationsLists = (
        categories: AutomationCategory[],
        capabilities: IntegrationCapability[],
    ) => {
        const automationsAfterFiltering = filterAutomations(sortedAutomations, categories, capabilities);
        const searchResult = filterAutomationsByValue(automationsAfterFiltering, searchValue);
        setFilteredAutomations(automationsAfterFiltering);
        setAutomations(searchResult);
    };

    React.useEffect(() => {
        const newCategorySet = new Set<AutomationCategory>();
        const newCapabilitiesSet = new Set<IntegrationCapability>();
        sortedAutomations.forEach((automation) => {
            const automationCategory = categoryForAutomation(automation.type);
            if (automationCategory) {
                newCategorySet.add(automationCategory);
            }
            const automationCapabilities = automation.requiredCapabilities?.reduce(
                (accumulator, capability) => accumulator.concat(capability),
                [],
            );
            if (automationCapabilities && automationCapabilities.length > 0) {
                for (const capability of automationCapabilities) {
                    newCapabilitiesSet.add(capability);
                }
            }
        });

        // add/remove categories and capabilities rather than replace the lists so that order is maintained
        const newAvailableCategories = [...availableCategories];
        newAvailableCategories.filter((category) => newCategorySet.has(category));
        for (const category of newCategorySet) {
            if (!newAvailableCategories.includes(category)) {
                newAvailableCategories.push(category);
            }
        }

        const newAvailableCababilities = [...availableCapabilities];
        newAvailableCababilities.filter((capability) => newCapabilitiesSet.has(capability));
        for (const capability of newCapabilitiesSet) {
            if (!newAvailableCababilities.includes(capability)) {
                newAvailableCababilities.push(capability);
            }
        }

        if (!isSameSet(new Set(availableCategories), new Set(newAvailableCategories))) {
            setAvailableCategories(newAvailableCategories);
        }
        if (!isSameSet(new Set(availableCapabilities), new Set(newAvailableCababilities))) {
            setAvailableCapabilities(newAvailableCababilities);
        }

        setFilteredAndSearchedAutomationsLists(categoryFilters, capabilityFilters);
    }, [sortedAutomations]);

    const onFilterChange = (categories: AutomationCategory[], capabilities: IntegrationCapability[]) => {
        // Filter with new categories and capabilities rather than using the state variables
        setFilteredAndSearchedAutomationsLists(categories, capabilities);

        setCategoryFilters(categories);
        setCapabilityFilters(capabilities);
    };

    React.useEffect(() => {
        if (searchValue === '') {
            setAutomations(filteredAutomations);
        } else {
            setFilteredAndSearchedAutomationsLists(categoryFilters, capabilityFilters);
        }
    }, [searchValue]);

    const [showAutomationSettings, setShowAutomationSettings] = React.useState(false);
    const [automationIdSelectedForSettings, setAutomationIdSelectedForSettings] = React.useState(null);
    const appSettingAutomations = useSelector((state: RootState) => selectSortedFilteredAutomations(state, appId));

    const onShowAutomationSettings = (automationId: string) => {
        const automation = appSettingAutomations.find((automation) => automation.id === automationId);

        if (!automation) {
            globalMessage.error(`Automation with id ${automationId} not found`);

            return;
        }

        setAutomationIdSelectedForSettings(automationId);
        setShowAutomationSettings(true);
    };

    const onCloseAutomationSettings = () => {
        setShowAutomationSettings(false);
    };
    const automationsListRef = React.useRef(null);

    React.useEffect(() => {
        if (scrollToAutomation && automationRefRecords[scrollToAutomation]) {
            const automationRef = automationRefRecords[scrollToAutomation];

            automationRef.scrollIntoView(true);
        }
    }, []);

    const automationsRefCallback = (node: HTMLDivElement, automationId: string) => {
        automationRefRecords[automationId] = node;
    };

    return (
        <div
            ref={automationsListRef}
            style={{
                flex: 1,
                overflowY: 'auto',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    zIndex: 0,
                    gap: '36px',
                    padding: '36px',
                    alignItems: 'flex-start',
                }}
            >
                <AutomationsToolboxFilterComponent
                    availableCategories={availableCategories}
                    availableCapabilities={availableCapabilities}
                    onFilterChange={onFilterChange}
                    updatedSelectedCategoryFilters={categoryFilters}
                    updatedSelectedCapabilityFilters={capabilityFilters}
                />
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        width: '100%',
                        gap: '24px',
                    }}
                >
                    <Space
                        style={{
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                        size={24}
                    >
                        <Search
                            placeholder="Search automations"
                            allowClear
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={(value) => {
                                setSearchValue(value);
                            }}
                            style={{ width: 400 }}
                        />
                        <AutomationsToolboxSortComponent />
                    </Space>
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            borderWidth: 1,
                            borderRadius: '8px',
                            borderStyle: 'solid',
                            borderColor: antGrey.grey5,
                            backgroundColor: antGrey.grey2,
                            gap: '36px',
                            padding: '36px',
                        }}
                    >
                        <Space
                            split={<Divider type="vertical" style={{ height: '24px', color: antGrey.grey5 }}></Divider>}
                        >
                            <Text style={{ color: antGrey.grey8, fontSize: '16px', fontWeight: 400 }}>
                                {`${automations.length} ${automations.length === 1 ? 'result' : 'results'}`}
                            </Text>
                            <Button
                                type="link"
                                style={{
                                    color: runwayColors.primary,
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    padding: 0,
                                    margin: 0,
                                }}
                                onClick={() => {
                                    clearFilters();
                                }}
                            >
                                Clear filters
                            </Button>
                        </Space>
                        <Space direction="vertical" size={24}>
                            {automations.length > 0 &&
                                automations.map((automation) => {
                                    return (
                                        <AutomationInfoComponent
                                            refCallback={(node) => {
                                                return automationsRefCallback(node, automation.id);
                                            }}
                                            key={automation.id}
                                            appId={appId}
                                            orgId={orgId}
                                            automation={automation}
                                            onShowAutomationSettings={onShowAutomationSettings}
                                            onShowConnectIntegrations={(capabilityType) => {
                                                return onShowConnectIntegrations(capabilityType, automation.type);
                                            }}
                                        />
                                    );
                                })}
                        </Space>
                    </div>
                </div>
            </div>
            {automationIdSelectedForSettings && (
                <AppAutomationSettings
                    onAutomationSelected={(automationId) => setAutomationIdSelectedForSettings(automationId)}
                    orgId={orgId}
                    automationId={automationIdSelectedForSettings}
                    appId={appId}
                    onClose={onCloseAutomationSettings}
                    open={showAutomationSettings}
                    onAfterClose={() => {
                        setAutomationIdSelectedForSettings(null);
                    }}
                />
            )}
        </div>
    );
};

const filterAutomations = (
    automations: Automation[],
    categoryFilters: AutomationCategory[],
    capabilityFilters: IntegrationCapability[],
) => {
    if (categoryFilters.length === 0 && capabilityFilters.length === 0) {
        return automations;
    }
    const filteredAutomations = automations.filter((automation) => {
        const automationCapabilities = automation.requiredCapabilities?.reduce(
            (accumulator, capability) => accumulator.concat(capability),
            [],
        );
        const automationCategory = categoryForAutomation(automation.type);
        if (automationCategory && categoryFilters.includes(automationCategory)) {
            return true;
        }
        if (automationCapabilities) {
            for (const capability of automationCapabilities) {
                if (capabilityFilters.includes(capability)) {
                    return true;
                }
            }
        }
        return false;
    });
    return filteredAutomations;
};

const filterAutomationsByValue = (automations: Automation[], value: string) => {
    return automations.filter((automation) => {
        return (
            automation.type.toLowerCase().includes(value.toLowerCase()) ||
            automation.name.toLowerCase().includes(value.toLowerCase()) ||
            automation.description.toLowerCase().includes(value.toLowerCase())
        );
    });
};
